import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { backcastingService, factorService, scenarioService } from '../services/api';

import VisionStep from './backcasting/VisionStep';
import DifferencesStep from './backcasting/DifferencesStep';
import TimelineStep from './backcasting/TimelineStep';
import AnalysisStep from './backcasting/AnalysisStep';
import ActionPlanStep from './backcasting/ActionPlanStep';
import TimelinePresentation from './backcasting/TimelinePresentation';
import ActionPlanPresentation from './backcasting/ActionPlanPresentation';

/**
 * Backcasting: es parteix del futur que es vol i es camina cap enrere fins avui.
 *
 * Un cas pot tenir-ne **més d'un**, i per això la pestanya obre amb una llista:
 * el camí cap al futur desitjable i el pla de contingència si no arriba són dos
 * exercicis diferents sobre el mateix cas. En una aula, també deixa que dos
 * grups treballin sense trepitjar-se.
 *
 * **La navegació entre passes és lliure**, al contrari que la del constructor
 * d'escenaris. En un taller es salta endavant i enrere constantment; l'indicador
 * de dalt diu què està fet i què no, però no impedeix res.
 */

const PASSES = [
  { id: 1, clau: 'step1' },
  { id: 2, clau: 'step2' },
  { id: 3, clau: 'step3' },
  { id: 4, clau: 'step4' },
  { id: 5, clau: 'step5' }
];

const Backcasting = ({ case_, collaboration }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const [exercises, setExercises] = useState([]);
  const [exercise, setExercise] = useState(null);      // l'obert, sencer
  const [scenarios, setScenarios] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [carregant, setCarregant] = useState(true);
  const [passa, setPassa] = useState(1);
  // Què s'està projectant: null, 'pla' o 'linia'. Cada cosa es presenta des
  // d'on viu —el pla des de la capçalera, la línia de temps des de la seva
  // passa— perquè un sol botó «Presenta» que hagués de triar entre dues coses
  // obligaria a preguntar-ho cada vegada.
  const [presentant, setPresentant] = useState(null);

  const caseId = case_?.id;

  // ── Càrrega ────────────────────────────────────────────────────────────────

  const carregaLlista = useCallback(async () => {
    if (!caseId) return;
    setCarregant(true);
    try {
      const [ex, esc, dr] = await Promise.all([
        backcastingService.getExercises(caseId),
        scenarioService.getCaseScenarios(caseId),
        factorService.getCaseDrivers(caseId)
      ]);
      setExercises(ex);
      setScenarios(esc);
      setDrivers(dr);
    } catch (error) {
      console.error('Error loading backcasting:', error);
      toast.error(t('backcasting.fetchError'));
    } finally {
      setCarregant(false);
    }
  }, [caseId, t]);

  useEffect(() => { carregaLlista(); }, [carregaLlista]);

  const obre = async (exerciseId) => {
    try {
      const sencer = await backcastingService.getExercise(exerciseId);
      setExercise(sencer);
      setPassa(1);
    } catch (error) {
      console.error('Error opening exercise:', error);
      toast.error(t('backcasting.fetchError'));
    }
  };

  // ── Presència ──────────────────────────────────────────────────────────────
  // Marca en quina passa és cadascú, igual que fa el constructor d'escenaris.
  // Així l'indicador de dalt del cas pot dir qui treballa on.
  useEffect(() => {
    if (!collaboration?.isConnected || !exercise) return;
    const etiqueta = `backcasting_${exercise.id}_step_${passa}`;
    collaboration.startEditing(etiqueta);
    return () => collaboration.stopEditing(etiqueta);
  }, [collaboration?.isConnected, exercise?.id, passa]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Canvis dels companys ───────────────────────────────────────────────────
  // S'apliquen directament sobre l'estat local, sense recarregar res: recarregar
  // en sec mentre l'altre escriu és el que feia saltar el cursor abans.
  useEffect(() => {
    const aplica = (event) => {
      const { entity, action, data, user } = event.detail || {};
      if (!user?.user_id || !currentUser?.id || user.user_id === currentUser.id) return;
      if (!entity || !entity.startsWith('backcasting_')) return;

      if (entity === 'backcasting_exercise') {
        setExercises(prev => {
          if (action === 'deleted') return prev.filter(x => x.id !== data.id);
          const i = prev.findIndex(x => x.id === data.id);
          if (i === -1) return [...prev, data];
          const copia = [...prev];
          copia[i] = { ...copia[i], ...data };
          return copia;
        });
        setExercise(prev => {
          if (!prev || prev.id !== data.id) return prev;
          return action === 'deleted' ? null : { ...prev, ...data };
        });
        return;
      }

      const llistes = {
        backcasting_difference: 'differences',
        backcasting_milestone: 'milestones',
        backcasting_action: 'actions'
      };
      const clau = llistes[entity];
      if (!clau) return;

      setExercise(prev => {
        if (!prev) return prev;

        // Reordenar arriba amb la llista d'identificadors, no amb cap fila:
        // s'apliquen les posicions noves sense tocar el contingut, que pot
        // estar-se editant en aquest mateix moment.
        if (action === 'reordered') {
          if (data.exercise_id !== prev.id) return prev;
          const pos = {};
          (data.order || []).forEach((id, i) => { pos[id] = i; });
          return {
            ...prev,
            milestones: prev.milestones.map(m => (
              pos[m.id] === undefined ? m : { ...m, position: pos[m.id] }
            ))
          };
        }

        if (action === 'deleted') {
          return { ...prev, [clau]: prev[clau].filter(x => x.id !== data.id) };
        }
        if (data.exercise_id !== prev.id) return prev;

        const i = prev[clau].findIndex(x => x.id === data.id);
        if (i === -1) return { ...prev, [clau]: [...prev[clau], data] };
        const copia = [...prev[clau]];
        copia[i] = { ...copia[i], ...data };
        return { ...prev, [clau]: copia };
      });
    };

    window.addEventListener('collaboration-entity-change', aplica);
    return () => window.removeEventListener('collaboration-entity-change', aplica);
  }, [currentUser?.id]);

  // ── Desar ──────────────────────────────────────────────────────────────────
  // Un sol embolcall per a tot: aplica el canvi a l'estat i, si el servidor el
  // rebutja, el desfà i avisa. Els encerts no diuen res —el «desat» de cada
  // camp ja ho fa— perquè un toast per desada faria la pantalla inservible.

  const aplicaLocal = (clau, fila) => setExercise(prev => {
    if (!prev) return prev;
    const i = prev[clau].findIndex(x => x.id === fila.id);
    if (i === -1) return { ...prev, [clau]: [...prev[clau], fila] };
    const copia = [...prev[clau]];
    copia[i] = fila;
    return { ...prev, [clau]: copia };
  });

  const treuLocal = (clau, id) => setExercise(prev => (
    prev ? { ...prev, [clau]: prev[clau].filter(x => x.id !== id) } : prev
  ));

  const patchExercici = async (canvis) => {
    const abans = exercise;
    setExercise(prev => ({ ...prev, ...canvis }));
    try {
      const nou = await backcastingService.updateExercise(exercise.id, canvis);
      setExercise(prev => ({ ...prev, ...nou }));
      setExercises(prev => prev.map(e => (e.id === nou.id ? { ...e, ...nou } : e)));
    } catch (error) {
      console.error('Error updating exercise:', error);
      setExercise(abans);
      toast.error(t('backcasting.saveError'));
    }
  };

  const fesCrud = (clau, api) => ({
    crea: async (dades) => {
      try {
        const fila = await api.create(exercise.id, dades);
        aplicaLocal(clau, fila);
        return fila;
      } catch (error) {
        console.error('Error creating row:', error);
        toast.error(t('backcasting.saveError'));
      }
    },
    patch: async (id, canvis) => {
      const abans = exercise[clau].find(x => x.id === id);
      if (abans) aplicaLocal(clau, { ...abans, ...canvis });
      try {
        const fila = await api.update(id, canvis);
        aplicaLocal(clau, fila);
      } catch (error) {
        console.error('Error updating row:', error);
        if (abans) aplicaLocal(clau, abans);
        toast.error(t('backcasting.saveError'));
      }
    },
    esborra: async (id) => {
      const abans = exercise[clau].find(x => x.id === id);
      treuLocal(clau, id);
      try {
        await api.remove(id);
      } catch (error) {
        console.error('Error deleting row:', error);
        if (abans) aplicaLocal(clau, abans);
        toast.error(t('backcasting.deleteError'));
      }
    }
  });

  const diferencies = fesCrud('differences', {
    create: backcastingService.createDifference,
    update: backcastingService.updateDifference,
    remove: backcastingService.deleteDifference
  });
  const fites = fesCrud('milestones', {
    create: backcastingService.createMilestone,
    update: backcastingService.updateMilestone,
    remove: backcastingService.deleteMilestone
  });
  const accions = fesCrud('actions', {
    create: backcastingService.createAction,
    update: backcastingService.updateAction,
    remove: backcastingService.deleteAction
  });

  const reordena = async (ordre) => {
    const abans = exercise.milestones;
    const pos = {};
    ordre.forEach((id, i) => { pos[id] = i; });
    setExercise(prev => ({
      ...prev,
      milestones: prev.milestones.map(m => ({ ...m, position: pos[m.id] ?? m.position }))
    }));
    try {
      const noves = await backcastingService.reorderMilestones(exercise.id, ordre);
      setExercise(prev => ({ ...prev, milestones: noves }));
    } catch (error) {
      console.error('Error reordering milestones:', error);
      setExercise(prev => ({ ...prev, milestones: abans }));
      toast.error(t('backcasting.saveError'));
    }
  };

  const creaExercici = async () => {
    try {
      const nou = await backcastingService.createExercise(caseId, {
        name: t('backcasting.newExerciseName'),
        horizon_label: case_?.time_horizon || ''
      });
      setExercises(prev => [...prev, nou]);
      setExercise(nou);
      setPassa(1);
    } catch (error) {
      console.error('Error creating exercise:', error);
      toast.error(t('backcasting.saveError'));
    }
  };

  const esborraExercici = async (id) => {
    if (!window.confirm(t('backcasting.confirmDeleteExercise'))) return;
    try {
      await backcastingService.deleteExercise(id);
      setExercises(prev => prev.filter(e => e.id !== id));
      setExercise(prev => (prev && prev.id === id ? null : prev));
    } catch (error) {
      console.error('Error deleting exercise:', error);
      toast.error(t('backcasting.deleteError'));
    }
  };

  // El nom que es veu d'un escenari: el seu, o la seva posició dins del cas.
  const nomEscenari = (scenarioId) => {
    const ordenats = [...scenarios].sort((a, b) => a.id - b.id);
    const i = ordenats.findIndex(x => x.id === scenarioId);
    if (i === -1) return t('backcasting.scenarioGone');
    return ordenats[i].name || t('scenarios.defaultName', { index: i + 1 });
  };

  // ── Estat de cada passa, per a l'indicador ─────────────────────────────────

  const passaFeta = (id) => {
    if (!exercise) return false;
    switch (id) {
      case 1: return !!(exercise.vision_statement || '').trim();
      case 2: return exercise.differences.length > 0;
      case 3: return exercise.milestones.length > 0;
      case 4: {
        const critiques = exercise.milestones.filter(m => m.is_critical);
        return critiques.length > 0 && critiques.every(m => m.te_analisi);
      }
      case 5: return exercise.actions.length > 0;
      default: return false;
    }
  };

  // ── Pintat ─────────────────────────────────────────────────────────────────

  if (carregant) {
    return <div className="py-12 text-center text-sm text-neutral-500">{t('common.loading')}</div>;
  }

  if (!exercise) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">{t('cases.backcasting')}</h2>
            <p className="mt-1 max-w-3xl text-sm text-neutral-600">{t('backcasting.tabLead')}</p>
          </div>
          <button
            type="button"
            onClick={creaExercici}
            className="rounded-md bg-accent-600 px-3 py-2 text-sm font-medium text-white hover:bg-accent-700"
          >
            {t('backcasting.newExercise')}
          </button>
        </div>

        {exercises.length === 0 ? (
          <p className="rounded-lg border border-dashed border-neutral-300 px-4 py-12 text-center text-sm text-neutral-500">
            {t('backcasting.noExercises')}
          </p>
        ) : (
          <ul className="space-y-3">
            {exercises.map(e => (
              <li key={e.id}
                  className="grid gap-4 rounded-lg border border-neutral-200 bg-white p-4 md:grid-cols-[1fr_200px_auto] md:items-center">
                <button type="button" onClick={() => obre(e.id)} className="text-left">
                  <h3 className="text-sm font-semibold text-neutral-900 hover:text-accent-700">
                    {e.name || t('backcasting.untitledExercise')}
                  </h3>
                  <p className="mt-1 text-sm text-neutral-600">
                    {/* Es mira `scenario_id`, no el nom: els escenaris que surten
                        del constructor no en tenen fins que algú els en posa un, i
                        mirant el nom un exercici ancorat sortiria com si no ho fos. */}
                    {/* Mateix criteri que a la passa 1: si l'escenari no té
                        nom, la seva **posició** dins del cas, no l'identificador
                        de la base de dades. */}
                    {e.scenario_id
                      ? t('backcasting.anchoredTo', { scenario: nomEscenari(e.scenario_id) })
                      : t('backcasting.ownVision')}
                    {e.horizon_label ? ` · ${e.horizon_label}` : ''}
                    {' · '}
                    {t('backcasting.timelineCount', { total: e.milestones_count, critical: e.critical_count })}
                  </p>
                </button>
                <div className="text-xs text-neutral-500">
                  {t('backcasting.analysedCount', { done: e.analysed_count, critical: e.critical_count })}
                  <span className="mt-1.5 block h-1 overflow-hidden rounded bg-neutral-200">
                    <span
                      className="block h-full bg-accent-500"
                      style={{ width: `${e.critical_count ? Math.round((e.analysed_count / e.critical_count) * 100) : 0}%` }}
                    />
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => esborraExercici(e.id)}
                  title={t('common.delete')}
                  aria-label={t('common.delete')}
                  className="justify-self-start rounded p-2 text-neutral-400 hover:bg-neutral-100 hover:text-error-dark md:justify-self-end"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setExercise(null)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('backcasting.backToList')}
        </button>

        <div className="flex items-center gap-3">
          <span className="text-sm text-neutral-500">{exercise.name}</span>
          {/* El pla d'acció es projecta des de la capçalera i des de qualsevol
              passa, com el «Presentar escenaris» del resum del cas: és el
              resultat de l'exercici i és el que se n'emporta qui escolta. */}
          {exercise.actions.length > 0 && (
            <button
              type="button"
              onClick={() => setPresentant('pla')}
              className="inline-flex items-center gap-2 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-9 0a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2M9 12l2 2 4-4" />
              </svg>
              {t('backcasting.presentPlan')}
            </button>
          )}
        </div>
      </div>

      {/* Indicador de passes. No bloqueja res: hi diu què està fet, i prou. */}
      <nav className="flex overflow-x-auto rounded-lg border border-neutral-200 bg-neutral-50">
        {PASSES.map(p => {
          const actual = p.id === passa;
          const feta = passaFeta(p.id);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setPassa(p.id)}
              aria-current={actual ? 'step' : undefined}
              className={`flex min-w-[10rem] flex-1 items-center gap-2 border-r border-neutral-200 px-3 py-2.5 text-left text-xs last:border-r-0 ${
                actual ? 'bg-white font-semibold text-neutral-900' : 'text-neutral-500 hover:bg-white/60'
              }`}
            >
              <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border font-mono text-[10px] font-bold ${
                actual
                  ? 'border-accent-600 bg-accent-600 text-white'
                  : feta ? 'border-accent-500 text-accent-600' : 'border-neutral-300 text-neutral-400'
              }`}>
                {p.id}
              </span>
              <span className="truncate">{t(`backcasting.${p.clau}Short`)}</span>
            </button>
          );
        })}
      </nav>

      <div className="rounded-lg border border-neutral-200 bg-white p-6">
        {passa === 1 && (
          <VisionStep
            exercise={exercise}
            scenarios={scenarios}
            drivers={drivers}
            onPatch={patchExercici}
          />
        )}
        {passa === 2 && (
          <DifferencesStep
            case_={case_}
            differences={exercise.differences}
            onCreate={diferencies.crea}
            onPatch={diferencies.patch}
            onDelete={diferencies.esborra}
          />
        )}
        {passa === 3 && (
          <TimelineStep
            exercise={exercise}
            milestones={exercise.milestones}
            onCreate={fites.crea}
            onPatch={fites.patch}
            onDelete={fites.esborra}
            onReorder={reordena}
            onPresent={() => setPresentant('linia')}
          />
        )}
        {passa === 4 && (
          <AnalysisStep
            milestones={exercise.milestones}
            onPatch={fites.patch}
          />
        )}
        {passa === 5 && (
          <ActionPlanStep
            exercise={exercise}
            actions={exercise.actions}
            milestones={exercise.milestones}
            onCreate={accions.crea}
            onPatch={accions.patch}
            onDelete={accions.esborra}
          />
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setPassa(p => Math.max(1, p - 1))}
          disabled={passa === 1}
          className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
        >
          {t('common.previous')}
        </button>
        <span className="text-xs text-neutral-500">
          {t('common.step', { current: passa, total: PASSES.length })}
        </span>
        {/* A la darrera passa aquí hi havia un «Següent» apagat i sense res a
            fer, que és la pitjor de les opcions: ocupa el lloc on l'ull busca
            la sortida i no en dona cap. Ara hi diu «Acaba» i torna a la llista
            d'exercicis, que és on es veu la feina feta. No marca cap estat: el
            pla d'acció ja és el resultat i l'exercici es pot reobrir sempre. */}
        {passa === PASSES.length ? (
          <button
            type="button"
            onClick={() => setExercise(null)}
            className="rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
          >
            {t('backcasting.finish')}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setPassa(p => Math.min(PASSES.length, p + 1))}
            className="rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
          >
            {t('common.next')}
          </button>
        )}
      </div>

      {presentant === 'pla' && (
        <ActionPlanPresentation
          case_={case_}
          exercise={exercise}
          actions={exercise.actions}
          milestones={exercise.milestones}
          onClose={() => setPresentant(null)}
        />
      )}

      {presentant === 'linia' && (
        <TimelinePresentation
          case_={case_}
          exercise={exercise}
          milestones={exercise.milestones}
          onClose={() => setPresentant(null)}
        />
      )}
    </div>
  );
};

export default Backcasting;
